import {FC, useEffect, useState, useRef} from "react";

interface ExplanationBoxProps {
  title?: string;
  content: string; // full HTML content provided as a string
  aiLogoSrc?: string;
  boxWidth?: string; // Tailwind classes for box width (default: "md:w-1/2")
  boxHeight?: string; // Tailwind classes for box height (default: "h-full")
  canToggle?: boolean; // controls whether toggle functionality is enabled (default: false)
  mobileCollapsed?: boolean; // controls whether content is collapsed on mobile by default (default: true)
}

const ExplanationBox: FC<ExplanationBoxProps> = ({
                                                   title = "AI Assistant",
                                                   content,
                                                   aiLogoSrc = "/images/shreds-ai-icon.png",
                                                   boxWidth = "md:w-1/2",
                                                   boxHeight = "h-auto",
                                                   canToggle = false,
                                                   mobileCollapsed = true,
                                                 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Check if we're on mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    // Check if content needs expansion when toggle is enabled or when on mobile
    const shouldEnableToggle = canToggle || (mobileCollapsed && isMobile);
    
    if (shouldEnableToggle && contentRef.current) {
      const scrollHeight = contentRef.current.scrollHeight;
      const clientHeight = contentRef.current.clientHeight;
      setNeedsExpansion(scrollHeight > clientHeight + 10); // 10px buffer
    }
  }, [content, canToggle, isMobile, mobileCollapsed]);
  
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Determine if toggle should be shown
  const shouldShowToggle = canToggle || (mobileCollapsed && isMobile);
  
  return (
    <div
      className={`relative rounded-lg overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 text-white p-3 sm:p-4 ${boxWidth} ${boxHeight} border border-gray-700`}>
      {/* Content */}
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center mb-2 sm:mb-3">
          <img src={aiLogoSrc} alt="AI Logo" className="w-5 h-5 sm:w-6 sm:h-6 mr-2"/>
          <h3 className="text-base sm:text-lg font-semibold">{title}</h3>
        </div>
        
        {/* Content container */}
        <div className="relative">
          <div
            ref={contentRef}
            className={`prose prose-sm prose-invert max-w-none text-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
              shouldShowToggle ? (isExpanded ? 'max-h-none' : 'max-h-16 sm:max-h-20') : 'max-h-none'
            }`}
            dangerouslySetInnerHTML={{__html: content}}
          />
          
          {/* Gradient overlay when collapsed */}
          {shouldShowToggle && !isExpanded && needsExpansion && (
            <div
              className="absolute bottom-0 left-0 w-full h-6 sm:h-8 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none"/>
          )}
        </div>
        
        {/* Compact toggle button */}
        {shouldShowToggle && needsExpansion && (
          <button
            onClick={toggleExpand}
            className="mt-2 self-start text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center group"
          >
            <span>{isExpanded ? "Show less" : "Show more"}</span>
            <svg
              className={`ml-1 w-3 h-3 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExplanationBox;